import {
  APP_INITIALIZER,
  Component,
  HostListener,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { AlertController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject, fromEvent, takeUntil } from 'rxjs';
import { AuthenticationService } from './services/authentication.service';
import { GlobalDataService } from './services/global-data.service';
import { AlertService } from './services/alert.service';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { App } from '@capacitor/app';
import { register } from 'swiper/element/bundle';
import { BehaviorSubject, Observable } from 'rxjs';
import { VaultService } from './services/vault.service';
import {
  IonApp,
  IonSplitPane,
  IonRouterOutlet,
  AlertButton
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  notifications,
  close,
  phonePortraitOutline,
  card,
  document as doc,
  cash,
  listCircle,
  cloudUpload,
  folder,
  arrowForward,
  contrastSharp,
  chatbubble,
  checkmarkCircle,
  locationSharp,
  call,
  mail,
  home,
  ellipsisHorizontal,
  person,
  timer,
  timeOutline,
  timerOutline,
  mailOutline,
  list,
  closeOutline,
  chevronDownOutline,
  chevronUpOutline,
  menu,
  flag,
  add,
  logoAndroid,
  logoApple,
  time,
  calendar,
  trash,
  contrast,
  flash,
  cropOutline,
  refreshOutline,
  contract,
  camera,
  cashOutline,
  cameraOutline,
  open,
  information,
  informationOutline,
  informationCircle,
  chevronForwardOutline,
  chevronForwardCircle,
  mailOpen,
  chevronBack,
  eyeOffOutline,
  eyeOutline,
  mailSharp,
  paperPlaneOutline,
  paperPlaneSharp,
  heartOutline,
  heartSharp,
  archiveOutline,
  archiveSharp,
  trashOutline,
  trashSharp,
  warningOutline,
  warningSharp,
  bookmarkOutline,
  bookmarkSharp,
  reorderTwoOutline,
  notificationsCircleOutline,
  print,
  cashSharp,
  informationCircleOutline,
  help,
  lockClosed,
  desktop,
  arrowDownCircle,
  ellipse,
  alertCircle
} from 'ionicons/icons';

import { USER_OBJECT } from './services/vault-service.constants';
import { Capacitor } from '@capacitor/core';
import { ClearCacheService } from './services/clear-cache.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpConfigInterceptor } from './services/http-interceptor';
import {
  ActionPerformed as PushActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import {
  ScheduleOptions, 
  LocalNotifications,
  ActionPerformed,
  LocalNotificationSchema
} from '@capacitor/local-notifications';
import { APPCONSTANTS } from './services/app-service.constants';
import { Preferences } from '@capacitor/preferences';

declare let IRoot: any;

export function clearIndexHtmlCache(cacheService: ClearCacheService) {
  return () => {
    cacheService.clearCacheForFile('index.html');
  };
}

register();

export interface StoredNotification {
  id: number;
  title: string;
  body: string;
  category: string;
  receivedAt: Date;
  data: any;
  isRead?: boolean;
  source?: 'push' | 'local';
}

// Enhanced Notification Service
export class NotificationStorageService {
  private readonly STORAGE_KEY = 'stored_notifications';
  private readonly MAX_NOTIFICATIONS = 100;

  async saveNotifications(notifications: StoredNotification[]): Promise<void> {
    try {
      const notificationsToStore = notifications.slice(0, this.MAX_NOTIFICATIONS);
      await Preferences.set({
        key: this.STORAGE_KEY,
        value: JSON.stringify(notificationsToStore)
      });
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }

  async loadNotifications(): Promise<StoredNotification[]> {
    try {
      const { value } = await Preferences.get({ key: this.STORAGE_KEY });
      if (value) {
        const notifications = JSON.parse(value);
        // Convert receivedAt back to Date objects
        return notifications.map((notif: any) => ({
          ...notif,
          receivedAt: new Date(notif.receivedAt)
        }));
      }
      return [];
    } catch (error) {
      console.error('Error loading notifications:', error);
      return [];
    }
  }

  async clearNotifications(): Promise<void> {
    try {
      await Preferences.remove({ key: this.STORAGE_KEY });
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  providers: [
    TranslateService,
    AlertController,
    HTTP,
    NotificationStorageService,
    {
      provide: APP_INITIALIZER,
      useFactory: clearIndexHtmlCache,
      deps: [ClearCacheService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
  ],
  imports: [
    RouterLink,
    RouterLinkActive,
    IonApp,
    IonSplitPane,
    IonRouterOutlet,
  ],
})
export class AppComponent implements OnInit {
  private globalDataVar: any;
  toggleOperator = 'add';
  userInactive: Subject<any> = new Subject();
  timeoutId: any;
  public appPages = [];
  private unsubscriber: Subject<void> = new Subject<void>();
  
  // Enhanced notification management
  private notificationsSubject = new BehaviorSubject<StoredNotification[]>([]);
  public notifications$: Observable<StoredNotification[]> = this.notificationsSubject.asObservable();
  
  // Track pending notifications for Android
  private pendingAndroidNotifications: Map<number, StoredNotification> = new Map();

  constructor(
    private router: Router,
    private translate: TranslateService,
    private alertCtrl: AlertController,
    private authenticationService: AuthenticationService,
    private alertService: AlertService,
    private clearCache: ClearCacheService,
    private platform: Platform,
    private httpSSL: HTTP,
    private vaultService: VaultService,
    public globalData: GlobalDataService,
    private notificationStorage: NotificationStorageService
  ) {
    this.addAllIcons();
    this.initializeApp();
    this.globalDataVar = globalData;
    
    fromEvent(window, 'popstate')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(async _ => {
        if (
          this.globalData.agreementPageVisitedCount > 1 ||
          this.globalData.homePageVisitedCount > 1
        ) {
          window.history.forward();
        }
      });
  }

  async ngOnInit() {
    await this.vaultService.init();
    
    // Load stored notifications on app start
    await this.loadStoredNotifications();
    
    try {
      await this.httpSSL.setServerTrustMode('pinned');
    } catch {
      if (
        Capacitor.getPlatform() === 'ios' ||
        Capacitor.getPlatform() === 'android'
      ) {
        await App.exitApp();
      }
      return;
    }
    
    if (typeof IRoot !== 'undefined' && IRoot) {
      IRoot.isRooted(
        async (data: any) => {
          if (data && data == 1) {
            await App.exitApp();
          }
        },
        async (data: any) => {}
      );
    }
    
    await this.processPushNotifications();
    await this.setupLocalNotifications();
  }

  private async loadStoredNotifications(): Promise<void> {
    try {
      const storedNotifications = await this.notificationStorage.loadNotifications();
      this.notificationsSubject.next(storedNotifications);
      console.log('Loaded stored notifications:', storedNotifications.length);
    } catch (error) {
      console.error('Error loading stored notifications:', error);
    }
  }

  private async setupLocalNotifications(): Promise<void> {
    try {
      // Request permissions for local notifications
      const permissions = await LocalNotifications.requestPermissions();
      console.log('Local notification permissions:', permissions);

      // Listen for local notification actions
      LocalNotifications.addListener('localNotificationActionPerformed', async (action: ActionPerformed) => {
        console.log('Local notification action performed:', action);
        await this.handleNotificationAction(action);
      });

      // Listen for local notification received (when app is in foreground)
      LocalNotifications.addListener('localNotificationReceived', (notification: LocalNotificationSchema) => {
        console.log('Local notification received in foreground:', notification);
      });

    } catch (error) {
      console.error('Error setting up local notifications:', error);
    }
  }

  private async handleNotificationAction(action: ActionPerformed): Promise<void> {
    const notificationId = action.notification.id;
    
    // Remove from pending notifications
    if (this.pendingAndroidNotifications.has(notificationId)) {
      this.pendingAndroidNotifications.delete(notificationId);
    }

    // Handle specific actions
    if (action.actionId === 'OPEN_LINK' && action.notification.extra?.outboundUrl) {
      const outboundUrl = action.notification.extra.outboundUrl;
      if (outboundUrl) {
        await Browser.open({ url: outboundUrl });
      }
    }

    // Mark notification as read if it exists
    await this.markNotificationAsRead(notificationId);
  }

  private initializeApp() {
    this.platform.ready().then(() => {
      this.platform.backButton.subscribeWithPriority(0, () => {
        App.minimizeApp();
      });
      
      window.addEventListener('keyboardDidShow', e => {
        const elementFocused: any = document.querySelector(':focus');
        if (elementFocused) {
          elementFocused.blur();
          elementFocused.focus();
        }
      });
    });
    
    this.translate.setDefaultLang('en');
    document.body.setAttribute('data-theme', 'light');
    document.body.classList.toggle('dark', false);
  }

  goTo(item: any) {
    if (item.sub === null) {
      if (item.external === false || item.external === undefined) {
        this.router.navigate([item.url]);
      } else {
        Browser.open({ url: item.url });
      }
    }
  }

  @HostListener('touchstart')
  @HostListener('window:keydown')
  @HostListener('window:mousedown')
  checkUserActivity() {
    if (!this.globalData.isLogout) {
      this.restartIdleLogoutTimer();
      this.checkTimeOut();
    }
  }

  private async checkTimeOut() {
    let userObject: any = await this.vaultService.getKeyValue(USER_OBJECT);
    if (userObject != null) {
      if (userObject.authToken && this.globalData.isLogout == false) {
        if (
          (await this.authenticationService.isAccessTokenActive()) === false
        ) {
          this.authenticationService.clearUserObject();
          await this.authenticationService.refreshToken().then(
            async data => {
              if (
                (await this.authenticationService.isAccessTokenActive()) ===
                false
              ) {
                this.logoutUser();
              }
            },
            async error => {
              this.logoutUser();
            }
          );
        }
      }
    }
  }

  private idleLogoutTimer: any;
  private restartIdleLogoutTimer() {
    clearTimeout(this.idleLogoutTimer);
    this.idleLogoutTimer = setTimeout(async () => {
      let message = `Your session is about to expire. Click Continue to keep session active`;
      await this.alertService.presentAlert('Alert', message);
    }, 900000); // 15 minutes
  }

  private async logoutUser() {
    this.globalData.isLogout = true;
    this.authenticationService.logout();
    let alert: any = await this.alertCtrl.create({
      header: 'Alert',
      message: 'Your Session Timed Out!',
      cssClass: 'custom-alert',
      buttons: [
        {
          text: 'Ok',
          role: 'Update',
          cssClass: 'alert-button-cancel',
          handler: async () => {
            alert?.dismiss();
            alert = null;
            await this.reload();
          },
        },
      ],
    });
    await alert.present();
    if (alert) {
      setTimeout(async () => {
        alert?.dismiss();
        await this.reload();
      }, 1000);
    }
  }

  private async reload() {
    await this.vaultService.clearVault();
    await this.authenticationService.isAuthenticated.next(false);
    await this.clearCache.clearCacheForFile('index.html');
    document.location.href = 'index.html';
  }

  async processPushNotifications() {
    // Request permission to use push notifications
    PushNotifications.requestPermissions().then(result => {
      if (result.receive === APPCONSTANTS.GRANTED) {
        PushNotifications.register();
      } else {
        console.error('Push notification permissions denied');
      }
    });

    // Listen for native notification dismissals (Android)
    if (Capacitor.getPlatform() === 'android') {
      this.setupNativeDismissalListener();
    }

    // Handle registration errors
    PushNotifications.addListener('registrationError',
      (error: any) => console.error('Push registration error', error)
    );

    // Handle successful registration
    PushNotifications.addListener('registration',
      (token: Token) => {
        console.log('Push registration success, token: ', token.value);
        // Send token to your server here
      }
    );

    // Enhanced push notification handling
    PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        console.log('Push notification received:', notification);
        
        const storedNotification: StoredNotification = {
          id: this.generateNotificationId(),
          title: notification.data?.title || notification.title || 'No Title',
          body: notification.data?.body || notification.body || 'No Body',
          category: notification.data?.category || 'DEFAULT',
          receivedAt: new Date(),
          data: notification.data || {},
          isRead: false,
          source: 'push'
        };

        // Add to stored notifications
        await this.addNotification(storedNotification);

        // Handle Android-specific behavior
        if (Capacitor.getPlatform() === 'android') {
          await this.handleAndroidNotification(storedNotification, notification);
        }
      }
    );

    // Handle push notification actions (iOS)
    PushNotifications.addListener('pushNotificationActionPerformed', 
      async (action: PushActionPerformed) => {
        console.log('Push notification action performed:', action);
        
        if (action.actionId === 'OPEN_LINK' && action.notification.data?.outboundUrl) {
          const outboundUrl = action.notification.data.outboundUrl;
          if (outboundUrl) {
            await Browser.open({ url: outboundUrl });
          }
        }
      }
    );
  }

  private async handleAndroidNotification(storedNotification: StoredNotification, originalNotification: PushNotificationSchema): Promise<void> {
    try {
      // Store reference to pending notification
      this.pendingAndroidNotifications.set(storedNotification.id, storedNotification);

      // Create enhanced local notification for Android
      const localNotificationOptions: ScheduleOptions = {
        notifications: [
          {
            id: storedNotification.id,
            title: storedNotification.title,
            body: storedNotification.body,
            actionTypeId: storedNotification.category,
            extra: {
              ...storedNotification.data,
              originalId: storedNotification.id,
              category: storedNotification.category
            },
            schedule: { at: new Date(Date.now() + 1000) }, // Schedule 1 second from now
            sound: 'default',
            smallIcon: 'ic_stat_icon_config_sample'
          }
        ]
      };

      // Add action buttons for specific categories
      if (storedNotification.category === 'OPEN_BROWSER_URL' && storedNotification.data?.outboundUrl) {
        localNotificationOptions.notifications[0].actionTypeId = 'OPEN_BROWSER_URL';
        localNotificationOptions.notifications[0].extra!.outboundUrl = storedNotification.data.outboundUrl;
      }

      const result = await LocalNotifications.schedule(localNotificationOptions);
      console.log('Local notification scheduled for Android:', result);

    } catch (error) {
      console.error('Error handling Android notification:', error);
    }
  }

  private generateNotificationId(): number {
    return Math.floor(Math.random() * 0xFFFFFFFF) * (Math.random() < 0.5 ? -1 : 1);
  }

  private async addNotification(notification: StoredNotification): Promise<void> {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = [notification, ...currentNotifications];
    
    // Keep only last 100 notifications to prevent memory issues
    if (updatedNotifications.length > 100) {
      updatedNotifications.splice(100);
    }
    
    this.notificationsSubject.next(updatedNotifications);
    
    // Save to persistent storage
    await this.notificationStorage.saveNotifications(updatedNotifications);
  }

  // Enhanced public methods for notification management
  getNotifications(): StoredNotification[] {
    return this.notificationsSubject.value;
  }

  async clearAllNotifications(): Promise<void> {
    this.notificationsSubject.next([]);
    await this.notificationStorage.clearNotifications();
    
    // Clear any pending local notifications on Android
    if (Capacitor.getPlatform() === 'android') {
      try {
        await LocalNotifications.cancel({ notifications: Array.from(this.pendingAndroidNotifications.keys()).map(id => ({ id })) });
        this.pendingAndroidNotifications.clear();
      } catch (error) {
        console.error('Error clearing local notifications:', error);
      }
    }
  }

  async removeNotification(id: number): Promise<void> {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.filter(notif => notif.id !== id);
    
    this.notificationsSubject.next(updatedNotifications);
    await this.notificationStorage.saveNotifications(updatedNotifications);
    
    // Remove from pending Android notifications
    if (this.pendingAndroidNotifications.has(id)) {
      this.pendingAndroidNotifications.delete(id);
      try {
        await LocalNotifications.cancel({ notifications: [{ id }] });
      } catch (error) {
        console.error('Error canceling local notification:', error);
      }
    }
  }

  async markNotificationAsRead(id: number): Promise<void> {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notif => 
      notif.id === id ? { ...notif, isRead: true } : notif
    );
    
    this.notificationsSubject.next(updatedNotifications);
    await this.notificationStorage.saveNotifications(updatedNotifications);
  }

  getNotificationCount(): number {
    return this.notificationsSubject.value.length;
  }

  getUnreadNotificationCount(): number {
    return this.notificationsSubject.value.filter(notif => !notif.isRead).length;
  }

  // Enhanced permission checking
  private async checkPermissions(): Promise<void> {
    try {
      let permStatus = await PushNotifications.checkPermissions();
      console.log('Push notification permissions:', permStatus);
      
      if (permStatus.receive === 'granted') {
        await PushNotifications.register();
        return;
      } else if (permStatus.receive === 'denied') {
        await this.actionPrompt(
          'Notification Permissions Denied',
          'Please check your device settings to enable notifications for this app!'
        );
        return;
      } else {
        // Request permissions
        const permissionStatus = await PushNotifications.requestPermissions();
        if (permissionStatus.receive === 'granted') {
          await PushNotifications.register();
        } else {
          await this.actionPrompt(
            'Notification Permissions Required',
            'This app needs notification permissions to keep you updated!'
          );
        }
      }

      // Also check local notification permissions
      const localPermissions = await LocalNotifications.checkPermissions();
      if (localPermissions.display !== 'granted') {
        await LocalNotifications.requestPermissions();
      }

    } catch (e) {
      console.error('Error checking permissions:', e);
    }
  }
  
  async actionPrompt(header: string, message?: string, actions?: (string | AlertButton)[], subHeader?: string) {
    const buttons = actions || ['OK'];
    const alert = await this.alertCtrl.create({
      mode: 'ios',
      header,
      subHeader,
      message,
      buttons
    });
    await alert.present();
  }

  private addAllIcons() {
    addIcons({
      notifications,
      information,
      informationOutline,
      informationCircle,
      close,
      card,
      doc,
      cash,
      listCircle,
      cloudUpload,
      folder,
      arrowForward,
      contrastSharp,
      chatbubble,
      checkmarkCircle,
      locationSharp,
      cashOutline,
      cashSharp,
      time,
      help,
      informationCircleOutline,
      timeOutline,
      timerOutline,
      timer,
      call,
      arrowDownCircle,
      lockClosed,
      desktop,
      mail,
      mailOutline,
      phonePortraitOutline,
      closeOutline,
      chevronUpOutline,
      chevronDownOutline,
      list,
      logoAndroid,
      menu,
      flag,
      print,
      logoApple,
      add,
      contrast,
      trash,
      calendar,
      person,
      ellipsisHorizontal,
      reorderTwoOutline,
      home,
      notificationsCircleOutline,
      cropOutline,
      flash,
      refreshOutline,
      contract,
      camera,
      cameraOutline,
      open,
      chevronForwardOutline,
      chevronForwardCircle,
      mailOpen,
      ellipse,
      eyeOffOutline,
      eyeOutline,
      chevronBack,
      mailSharp,
      paperPlaneOutline,
      paperPlaneSharp,
      heartOutline,
      heartSharp,
      archiveOutline,
      archiveSharp,
      trashOutline,
      trashSharp,
      warningOutline,
      warningSharp,
      bookmarkOutline,
      bookmarkSharp,
      alertCircle,
      'hand-shake': 'assets/icon/handshake-solid.svg',
      'layer-group': 'assets/icon/layer-group-solid.svg',
      'bell-solid': 'assets/icon/bell-solid.svg',
      'bell-orange': 'assets/icon/bell-orange.svg',
      'money-solid': 'assets/icon/money-solid.svg',
      'clock-full': 'assets/icon/clock-full.svg',
      'upload-solid': 'assets/icon/upload-solid.svg',
      'id-card-solid': 'assets/icon/id-card-solid.svg',
      'file-circle-question-solid': 'assets/icon/file-circle-question-solid.svg',
      'rectangle-list-solid': 'assets/icon/rectangle-list-solid.svg',
      'list-check-solid': 'assets/icon/list-check-solid.svg',
      'circle-question-solid': 'assets/icon/circle-question-solid.svg',
      'list-check': 'assets/icon/list-check-solid.svg',
      'faq-solid': 'assets/icon/faq-solid.svg',
      'helpful-solid': 'assets/icon/helpful-solid.svg',
      'technical-solid': 'assets/icon/technical-solid.svg',
      'license-status-solid': 'assets/icon/license-status-solid.svg',
      'license-release-solid': 'assets/icon/license-release-solid.svg'
    });
  }

  private setupNativeDismissalListener() {
    // Listen for broadcasts from native Android code when notifications are dismissed
    if (Capacitor.getPlatform() === 'android') {
      // This would require a Capacitor plugin to listen for Android broadcasts
      // For now, we'll rely on the Ionic-side notification management
      console.log('Native dismissal listener setup for Android');
    }
  }
}